// WDC Global Stripe Billing Library
// Hybrid pricing: base subscription + usage overages + outcome bonuses

import { Product, ProductId, SubscriptionTier, BillingEvent, User } from '../types';

export const PRODUCTS: Record<ProductId, Product> = {
  'command-center': {
    id: 'command-center',
    name: 'Command Center',
    description: 'AI Agent Suite Hub - Central orchestration for all WDC products',
    icon: '🎛️',
    color: '#8b5cf6',
    route: '/command-center',
    enabled: true,
    tierRequired: 'free',
    features: [
      'Unified dashboard',
      'Cross-product analytics',
      'AI worker orchestration',
      'Profit loop management',
      'Team collaboration',
      'API access',
    ],
    pricing: {
      monthly: 0,
      yearly: 0,
      unit: 'seat',
      includedUsage: 1000,
      overagePrice: 0.001,
    },
  },
  'agentguard': {
    id: 'agentguard',
    name: 'AgentGuard',
    description: 'AI Agent Identity, Authorization & Audit',
    icon: '🛡️',
    color: '#00A6FF',
    route: '/agentguard',
    enabled: true,
    tierRequired: 'starter',
    features: [
      'Agent identity management',
      'Authorization policies',
      'Real-time audit trail',
      'Compliance reporting',
      'Threat detection',
      'Multi-agent orchestration',
    ],
    pricing: {
      monthly: 99,
      yearly: 990,
      unit: 'agent',
      includedUsage: 10,
      overagePrice: 5.00,
    },
  },
  'complianceos': {
    id: 'complianceos',
    name: 'ComplianceOS',
    description: 'All-in-One Regulatory Compliance Platform',
    icon: '📋',
    color: '#0f172a',
    route: '/complianceos',
    enabled: true,
    tierRequired: 'professional',
    features: [
      'Framework mapping (SOC2, ISO27001, GDPR, HIPAA)',
      'Automated assessments',
      'Control management',
      'Evidence collection',
      'Continuous monitoring',
      'Audit-ready reports',
    ],
    pricing: {
      monthly: 299,
      yearly: 2990,
      unit: 'workflow',
      includedUsage: 50,
      overagePrice: 2.00,
    },
  },
  'permitflow': {
    id: 'permitflow',
    name: 'PermitFlow AI',
    description: 'Autonomous Construction Permit & Compliance',
    icon: '🏗️',
    color: '#f97316',
    route: '/permitflow',
    enabled: true,
    tierRequired: 'professional',
    features: [
      'Permit automation',
      'Jurisdiction intelligence',
      'Document generation',
      'Status tracking',
      'Deadline management',
      'Multi-project portfolio',
    ],
    pricing: {
      monthly: 199,
      yearly: 1990,
      unit: 'workflow',
      includedUsage: 5,
      overagePrice: 15.00,
    },
  },
  'regwatch': {
    id: 'regwatch',
    name: 'RegWatch',
    description: 'Cross-Jurisdiction Regulatory Intelligence',
    icon: '👁️',
    color: '#8b5cf6',
    route: '/regwatch',
    enabled: true,
    tierRequired: 'starter',
    features: [
      'Real-time regulatory monitoring',
      'Impact assessments',
      'Custom alerts',
      'Multi-jurisdiction coverage',
      'API webhooks',
      'Compliance calendar',
    ],
    pricing: {
      monthly: 149,
      yearly: 1490,
      unit: 'resolution',
      includedUsage: 100,
      overagePrice: 0.50,
    },
  },
};

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, { name: string; limits: any }> = {
  free: { name: 'Free', limits: { products: 1, workers: 3, profitLoops: 1, apiCalls: 100 } },
  starter: { name: 'Starter', limits: { products: 3, workers: 10, profitLoops: 5, apiCalls: 1000 } },
  professional: { name: 'Professional', limits: { products: 5, workers: 25, profitLoops: 10, apiCalls: 10000 } },
  enterprise: { name: 'Enterprise', limits: { products: 5, workers: 100, profitLoops: 50, apiCalls: 100000 } },
  custom: { name: 'Custom', limits: { products: 5, workers: 500, profitLoops: 200, apiCalls: 1000000 } },
};

export interface StripeConfig {
  secretKey: string;
  publishableKey: string;
  webhookSecret: string;
  apiVersion: string;
}

export interface CreateCheckoutSessionParams {
  userId: string;
  productId: ProductId;
  tier: SubscriptionTier;
  billingCycle: 'monthly' | 'yearly';
  successUrl: string;
  cancelUrl: string;
  metadata?: Record<string, string>;
}

export interface UsageRecordParams {
  userId: string;
  productId: ProductId;
  quantity: number;
  timestamp?: number;
  action: 'increment' | 'set';
}

export class StripeBilling {

  constructor(_config: StripeConfig) {
    // In production: this._stripe = require('stripe')(config.secretKey, { apiVersion: config.apiVersion });
  }

  async createCustomer(user: User): Promise<string> {
    return `cus_${user.id}`;
  }

  async createCheckoutSession(_params: CreateCheckoutSessionParams): Promise<{ sessionId: string; url: string }> {
    
    return { sessionId: `cs_${Date.now()}`, url: `https://checkout.stripe.com/pay/cs_${Date.now()}` };
  }

  async recordUsage(_params: UsageRecordParams): Promise<void> {
    // Implementation here
  }

  async createPortalSession(_customerId: string, _returnUrl: string): Promise<string> {
    return `https://billing.stripe.com/session/${Date.now()}`;
  }

  async handleWebhook(_payload: string, _signature: string): Promise<BillingEvent> {
    return {
      id: `evt_${Date.now()}`,
      userId: 'user_123',
      productId: 'command-center',
      type: 'subscription_created',
      amountUsd: 99,
      currency: 'usd',
      stripeEventId: `evt_${Date.now()}`,
      metadata: {},
      createdAt: new Date(),
    };
  }

  calculateUsageCost(productId: ProductId, usage: number): number {
    const product = PRODUCTS[productId];
    const included = product.pricing.includedUsage;
    const overage = Math.max(0, usage - included);
    return overage * product.pricing.overagePrice;
  }

  getProduct(productId: ProductId): Product | undefined {
    return PRODUCTS[productId];
  }

  getAllProducts(): Product[] {
    return Object.values(PRODUCTS);
  }

  getTierLimits(tier: SubscriptionTier) {
    return SUBSCRIPTION_TIERS[tier]?.limits || SUBSCRIPTION_TIERS.free.limits;
  }
}

export const stripeBilling = new StripeBilling({
  secretKey: process.env.STRIPE_SECRET_KEY || 'sk_test_...',
  publishableKey: process.env.STRIPE_PUBLISHABLE_KEY || 'pk_test_...',
  webhookSecret: process.env.STRIPE_WEBHOOK_SECRET || 'whsec_...',
  apiVersion: '2024-12-18.acacia',
});